import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DocumentSourceLinkIcon } from 'src/components/Reusable/Sources/sources.styles'
import { ModalFormId } from 'src/components/ModalForms/ModalForm'
import { ModalForm } from 'src/components/ModalForms/ModalForm'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { StyledInputError } from 'src/components/Reusable/Inputs/TextInput/styles'
import { Spinner } from 'src/Throbber'
import {
  ChatContextBooleanSelectorText,
  ChatContextBooleanSelectorPrompt,
  ChatContextBooleanSelectorContainer,
  ChatContextBooleanCheckboxContainer,
  ContextUpdateStatusContainer,
  ContextTypeSelectorSummary,
  ContextTypeSelectorSummaryContainer,
  LinkItem,
} from './chatContextPanel.styles'

export type ChatContextBooleanSelectorProps = {
  title: string
  description: React.ReactNode
  prompt: string
  currentSelection: boolean
  onCommit: (newSelection: boolean) => void
  show: boolean
  hide: () => void
  onBack?: () => void
}

export const ChatContextBooleanSelector = ({
  title,
  description,
  prompt,
  currentSelection,
  onCommit,
  show,
  hide: originalHide,
  onBack: originalOnBack,
}: ChatContextBooleanSelectorProps): JSX.Element => {
  const [includeAll, setIncludeAll] = useState(currentSelection)

  useEffect(() => {
    setIncludeAll(currentSelection)
  }, [currentSelection])

  const reset = useCallback(() => {
    setIncludeAll(currentSelection)
  }, [currentSelection])

  const hide = useCallback(() => {
    reset()
    originalHide()
  }, [reset, originalHide])

  const onBack = useCallback(() => {
    reset()
    originalOnBack?.()
  }, [reset, originalOnBack])

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onCommit(includeAll)
      if (originalOnBack) {
        onBack()
      } else {
        hide()
      }
    },
    [includeAll, onCommit, originalOnBack, onBack, hide],
  )

  const handleChange = useCallback(() => {
    setIncludeAll((prev) => !prev)
  }, [])

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={ModalFormId.CHAT_CONTEXT_BOOLEAN_SELECTOR}
      size="lg"
      title={title}
      submitText={originalOnBack ? 'Continue' : 'Confirm'}
      showOverflow
      hideBack={!originalOnBack}
      onBack={onBack}
    >
      <form id={ModalFormId.CHAT_CONTEXT_BOOLEAN_SELECTOR} onSubmit={onSubmit}>
        <ChatContextBooleanSelectorContainer>
          <ChatContextBooleanSelectorText>
            {description}
          </ChatContextBooleanSelectorText>
          <ChatContextBooleanCheckboxContainer>
            <CheckBoxInput
              id={'boolean-selector-checkbox'}
              checked={includeAll}
              onClick={(e) => e.stopPropagation()}
              onChange={handleChange}
            />
            <ChatContextBooleanSelectorPrompt>
              {prompt}
            </ChatContextBooleanSelectorPrompt>
          </ChatContextBooleanCheckboxContainer>
        </ChatContextBooleanSelectorContainer>
      </form>
    </ModalForm>
  )
}

export type StatusPanelProps = {
  busy: boolean
  error: string | undefined | null
}
export const StatusPanel = ({ busy, error }: StatusPanelProps): JSX.Element => {
  return (
    <ContextUpdateStatusContainer>
      {busy && <Spinner size="m" color="primary" />}
      {error && (
        <StyledInputError showError={!!error}>{error}</StyledInputError>
      )}
    </ContextUpdateStatusContainer>
  )
}

export type SummaryProps = {
  summary: string | string[]
  centerAligned?: boolean
}

export const Summary = ({
  summary,
  centerAligned,
}: SummaryProps): JSX.Element => {
  const summaryLines = useMemo(
    () => (Array.isArray(summary) ? summary : [summary]),
    [summary],
  )

  return (
    <ContextTypeSelectorSummaryContainer>
      {summaryLines.map((line, index) => (
        <ContextTypeSelectorSummary key={index} $centerAligned={centerAligned}>
          {line}
        </ContextTypeSelectorSummary>
      ))}
    </ContextTypeSelectorSummaryContainer>
  )
}

export type GridRowLinkItemProps = {
  link: string
  text: string
}

export const GridRowLinkItem = ({
  link,
  text,
}: GridRowLinkItemProps): JSX.Element => {
  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
  }, [])

  return (
    <LinkItem to={link} target="_blank" rel="noreferrer" onClick={onClick}>
      {text} <DocumentSourceLinkIcon />
    </LinkItem>
  )
}
